import { Link } from "react-router-dom";
import PageBanner from "../../PageBanner";
import SocialMediaSidebar from "../../SocialMediaSidebar";
import SectionHeader from "../../SectionHeader";
import policyImage from "../../../assets/images-webp/blog3.webp"; // Add your image path
import ScrollToTopButton from "../../ScrollToTopButton";
import SectionHeaderNoPDF from "../../SectionHeaderNoPDF";

const WasteManagementPolicy = () => {
  return (
    <>
      <div>
        <SocialMediaSidebar />
      </div>
      <PageBanner
        title="Policy Changes Shaping Waste Management"
        breadcrumb={[
          { label: "Home", href: "/" },
          { label: "Resources" },
          { label: "Blogs" },
        ]}
      />
      <div className="max-w-6xl mx-auto p-4">
        <SectionHeaderNoPDF
          title="Policy Changes Shaping Waste Management @ AJEEVI"
          shareUrl="https://swmecom.ajeevi.in/blog/policy-changes-shaping-waste-management"
        />
      </div>
      <div>
        <ScrollToTopButton />
      </div>

      {/* Page content */}
      <div className="w-full px-4 sm:px-6 md:px-10 lg:px-28 py-10 bg-white text-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Summary Section with Image */}
          <div className="flex flex-col lg:flex-row gap-8 mb-10">
            <div className="lg:w-2/3">
              <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                Summary
              </h4>
              <p className="text-black mb-4 text-lg">
                Recent policy shifts are focused on decentralization, technology adoption, accountability, and circular economy practices. By redefining responsibilities and compliance expectations for local bodies, producers, and citizens, these policies pave the way for efficient segregation, recycling, and scientific disposal.
              </p>
              <p className="text-black text-lg">
                With mandatory source segregation, extended producer responsibility (EPR), and digital monitoring systems becoming standard, the Indian waste management sector is entering a new phase of regulatory sophistication.
              </p>
            </div>
            <div className="lg:w-1/3">
              <img 
                src={policyImage} 
                alt="Waste Management Policy Changes" 
                className="w-full h-auto rounded-lg shadow-lg object-cover"
              />
              <p className="text-sm text-gray-500 mt-2 italic">
                Policy frameworks driving sustainable waste management practices across India
              </p>
            </div>
          </div>

          {/* Introduction Section */}
          <div className="mb-10">
            <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
              Introduction
            </h4>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="md:col-span-2">
                <p className="text-black mb-4 text-lg">
                  Waste management in India has witnessed a paradigm shift in recent years, driven by an evolving policy landscape that emphasizes sustainability, segregation at source, and technological integration. As urban populations grow and waste volumes surge, traditional collection and disposal methods are no longer sufficient.
                </p>
                <p className="text-black text-lg">
                  From Swachh Bharat Mission 2.0 to the Plastic Waste Management Rules, these regulatory changes are shaping operational frameworks, compliance mechanisms, and citizen participation models. In this blog, we explore the key policy changes that are setting the direction for smarter, cleaner, and more efficient waste management across India.
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-[#00D084]">
                <h5 className="font-bold text-lg mb-2">Did You Know?</h5>
                <p className="text-gray-700">
                  India generates approximately 62 million tonnes of waste annually, with policy changes aiming to increase processing from current 30% to 100% by 2030.
                </p>
              </div>
            </div>
          </div>

          {/* Key Policy Drivers Section */}
          <div className="mb-10">
            <h5 className="text-xl font-semibold mb-4 text-black">
              Key Policy Drivers in Waste Management:
            </h5>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border-l-4 border-blue-500">
                  <h6 className="font-semibold text-lg mb-2">Swachh Bharat Mission 2.0 (SBM-U 2.0)</h6>
                  <p className="text-gray-700">
                    Focuses on source segregation, waste processing, and zero-landfill goals through urban local body performance incentives and citizen engagement.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border-l-4 border-green-500">
                  <h6 className="font-semibold text-lg mb-2">Plastic Waste Management Rules (Amended 2022)</h6>
                  <p className="text-gray-700">
                    Mandates EPR compliance for producers, phase-out of single-use plastics, and encourages recycling and reuse of plastic packaging waste.
                  </p>
                </div>
              </div>
              <div>
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border-l-4 border-purple-500">
                  <h6 className="font-semibold text-lg mb-2">Solid Waste Management Rules (2016)</h6>
                  <p className="text-gray-700">
                    Enforces door-to-door collection, segregation at source, and involvement of informal waste pickers, along with penal actions for non-compliance.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border-l-4 border-red-500">
                  <h6 className="font-semibold text-lg mb-2">Bio-Medical & E-Waste Rules</h6>
                  <p className="text-gray-700">
                    Regulate proper disposal of hazardous waste with strict guidelines for generators, collectors, and processors using barcode-based tracking.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Policy Impact Visual */}
          <div className="mb-10 bg-gray-50 p-6 rounded-lg">
            <h5 className="text-xl font-semibold mb-4 text-black text-center">
              Policy Impact at a Glance
            </h5>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-lg shadow text-center">
                <div className="text-3xl font-bold text-[#00D084] mb-2">100%</div>
                <p className="text-gray-700">Urban Local Bodies now under SWM Rules</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow text-center">
                <div className="text-3xl font-bold text-[#00D084] mb-2">3.5x</div>
                <p className="text-gray-700">Increase in waste processing capacity since 2016</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow text-center">
                <div className="text-3xl font-bold text-[#00D084] mb-2">₹10K+ Cr</div>
                <p className="text-gray-700">Investments in waste infrastructure since SBM launch</p>
              </div>
            </div>
          </div>

          {/* Conclusion Section */}
          <div className="mb-10">
            <h5 className="text-xl font-semibold mb-3 text-black">Conclusion</h5>
            <div className="flex flex-col md:flex-row gap-6">
              <div className="md:w-2/3">
                <p className="text-black text-lg">
                  Policy innovation is central to building resilient and efficient waste ecosystems. With stronger enforcement mechanisms, digital tracking, and a focus on behavioral change, these reforms are pushing India closer to its sustainability goals. The transition from traditional to data-driven and decentralized systems will not only reduce environmental impact but also create employment and promote responsible consumption.
                </p>
              </div>
              <div className="md:w-1/3 bg-[#00D084] bg-opacity-10 p-4 rounded-lg">
                <h6 className="font-semibold text-lg mb-2">Looking Ahead</h6>
                <p className="text-gray-700">
                  Future policies are expected to focus on circular economy models, carbon credits for waste management, and AI-powered compliance monitoring.
                </p>
              </div>
            </div>
          </div>

          {/* Ajeevi's Contribution Section */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h5 className="text-xl font-semibold mb-3 text-black">
              Ajeevi's Contribution
            </h5>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-black mb-4 text-lg">
                  Ajeevi Technologies helps urban local bodies and state agencies align with new policy mandates through robust tech solutions. Our policy-compliant modules are tailored to deliver seamless data tracking, segregation audits, and performance dashboards.
                </p>
                <p className="text-black text-lg">
                  With Ajeevi, municipalities can turn policy into action. Learn more at{" "}
                  <a
                    href="http://www.ajeevi.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#00D084] hover:underline"
                  >
                    www.ajeevi.com
                  </a>.
                </p>
              </div>
              <div>
                <ul className="list-disc pl-6 space-y-2 text-lg text-black">
                  <li>
                    <strong>Policy-Aligned Software:</strong> Modules designed in line with SWM, PWM, and EPR compliance needs.
                  </li>
                  <li>
                    <strong>Real-time Compliance Reports:</strong> Generate reports for ULBs, CPCB/SPCB, and state authorities with ease.
                  </li>
                  <li>
                    <strong>Digital Grievance Redressal:</strong> Citizen-centric systems to support public feedback and issue resolution.
                  </li>
                  <li>
                    <strong>Data Analytics & Insights:</strong> Monitor segregation efficiency and collection frequency with actionable KPIs.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WasteManagementPolicy;